import { useState, useEffect, lazy, Suspense, Component, useCallback } from 'react'
import { supabase } from './lib/supabase'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import AppLayout from './components/Layout/AppLayout'
import PagePlaceholder from './components/PagePlaceholder/PagePlaceholder'

const safeLocalStorage = {
  getItem(key) {
    try {
      return localStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  setItem(key, value) {
    try {
      localStorage.setItem(key, value)
    } catch (e) {}
  },
  removeItem(key) {
    try {
      localStorage.removeItem(key)
    } catch (e) {}
  }
}

const safeSessionStorage = {
  getItem(key) {
    try {
      return sessionStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  setItem(key, value) {
    try {
      sessionStorage.setItem(key, value)
    } catch (e) {}
  },
  removeItem(key) {
    try {
      sessionStorage.removeItem(key)
    } catch (e) {}
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo)
  }

  handleReload = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('t', Date.now().toString())
    url.searchParams.set('retry', '1')
    window.location.href = url.toString()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-loading" style={{ padding: '24px', textAlign: 'center', flexDirection: 'column', gap: '16px', justifyContent: 'center', height: '100vh', background: 'var(--background-default)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Что-то пошло не так</h2>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '400px', fontSize: '14px', lineHeight: 1.5, margin: 0 }}>
            Возможно, приложение было обновлено. Пожалуйста, нажмите кнопку ниже, чтобы перезагрузить и обновить систему.
          </p>
          <button className="btn btn-primary" onClick={this.handleReload} style={{ padding: '10px 20px', fontSize: '14px' }}>
            Обновить приложение
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

const lazyWithRetry = (componentImport) => {
  return lazy(async () => {
    try {
      return await componentImport()
    } catch (error) {
      const url = new URL(window.location.href)
      const hasRetried = url.searchParams.get('retry') === '1'
      if (!hasRetried) {
        console.error("Chunk load failed, reloading with cache bust...", error)
        url.searchParams.set('retry', '1')
        url.searchParams.set('t', Date.now().toString())
        window.location.href = url.toString()
        return new Promise(() => {}) // Infinite promise to prevent rendering during reload
      }
      throw error
    }
  })
}

const LoginPage = lazyWithRetry(() => import('./pages/Login/LoginPage'))
const LandingPage = lazyWithRetry(() => import('./pages/Landing/LandingPage'))
import OperationsPage from './pages/Operations/OperationsPage'
import PaymentsPage from './pages/Payments/PaymentsPage'
import SettingsPage from './pages/Settings/SettingsPage'
const ChatPage = lazyWithRetry(() => import('./pages/Chat/ChatPage'))
import SchedulePage from './pages/Schedule/SchedulePage'
import ViolationsPage from './pages/Violations/ViolationsPage'
import DashboardPage from './pages/Dashboard/DashboardPage'
const AiAssistantPage = lazyWithRetry(() => import('./pages/AiAssistant/AiAssistantPage'))
import AnalyticsPage from './pages/Analytics/AnalyticsPage'
import TasksPage from './pages/Tasks/TasksPage'
import CamerasPage from './pages/Cameras/CamerasPage'
import VacanciesPage from './pages/Vacancies/VacanciesPage'
import SupportPage from './pages/Support/SupportPage'
const SuperAdminPage = lazyWithRetry(() => import('./pages/SuperAdmin/SuperAdminPage'))
import DailyReportPage from './pages/DailyReport/DailyReportPage'
import ReportsPage from './pages/Reports/ReportsPage'
import PinLockScreen from './components/PinLockScreen/PinLockScreen'
import PinSetupScreen from './components/PinLockScreen/PinSetupScreen'

import { TourProvider } from './contexts/TourContext'
import TourGuide from './components/TourGuide/TourGuide'

import PublicVacancyPage from './pages/Vacancies/PublicVacancyPage'
import PublicResponseStatusPage from './pages/Vacancies/PublicResponseStatusPage'

export const VALID_NAV_PAGES = new Set([
  'dashboard', 'reports', 'operations', 'payments', 'schedule',
  'analytics', 'tasks', 'cameras', 'vacancies', 'support', 'chat',
  'ai_assistant', 'settings', 'superadmin'
])

export function resolveDailyReportBackTarget(pageParams, historyState, sessionState, isManagement) {
  let parsedSession = null
  if (typeof sessionState === 'string') {
    try {
      parsedSession = JSON.parse(sessionState)
    } catch {
      parsedSession = null
    }
  } else if (sessionState && typeof sessionState === 'object') {
    parsedSession = sessionState
  }

  // Validate identity of sessionState against current report identity
  const currentPvzId = pageParams?.pvzId || historyState?.pvzId || null
  const currentDate = pageParams?.date || historyState?.date || null

  if (parsedSession) {
    if (parsedSession.pvzId && currentPvzId && parsedSession.pvzId !== currentPvzId) {
      parsedSession = null
    } else if (parsedSession.date && currentDate && parsedSession.date !== currentDate) {
      parsedSession = null
    }
  }

  // If pageParams explicitly lacks returnTo (e.g. direct entry or deep link with pvzId/date but no returnTo),
  // and historyState also lacks returnTo, sessionState MUST NOT resurrect a stale return context!
  const hasExplicitEntryWithoutReturn = Boolean(
    pageParams &&
    (pageParams.pvzId || pageParams.date) &&
    !pageParams.returnTo &&
    !historyState?.returnTo
  )

  const candidate = (pageParams?.returnTo ? pageParams : null)
    || (historyState?.returnTo ? historyState : null)
    || (!hasExplicitEntryWithoutReturn && parsedSession?.returnTo ? parsedSession : null)

  const defaultPage = isManagement ? 'dashboard' : 'operations'

  if (candidate && candidate.returnTo) {
    const targetPage = candidate.returnTo
    if (VALID_NAV_PAGES.has(targetPage)) {
      if (!isManagement && (targetPage === 'dashboard' || targetPage === 'reports')) {
        return { page: 'operations', params: null }
      }
      const returnParams = candidate.returnParams || (candidate.pvzId ? { pvzId: candidate.pvzId } : null)
      return {
        page: targetPage,
        params: returnParams
      }
    }
  }

  return { page: defaultPage, params: null }
}

function AppContent() {
  const { user, profile, loading, signOut, isDemoMode, demoLogin, fetchProfile } = useAuth()

  // Demo mode — create fake profile for demo
  const effectiveProfile = isDemoMode ? {
    id: 'demo-owner-id',
    name: 'Демо Владелец',
    full_name: 'Демо Владелец',
    email: 'demo@pvzraschet.ru',
    login: 'demo',
    role: 'owner',
    is_approved: true,
    pin_hash: null,
    pvz_id: 'demo-pvz-id',
    org: { admin_permissions: {}, employee_permissions: {}, helper_permissions: {} }
  } : profile;

  const isManagement = ['owner', 'admin', 'admin_employee'].includes(effectiveProfile?.role)

  const [showAuthForm, setShowAuthForm] = useState(false)
  const [activePage, setActivePage] = useState(() => {
    // Если вернулись с OAuth Ivideon callback — сразу на настройки
    if (window.location.pathname === '/ivideon-callback') return 'settings'
    if (window.location.pathname === '/daily-report') return 'daily_report'
    if (window.location.pathname === '/reports') return 'reports'
    return safeLocalStorage.getItem('wb_active_page') || 'dashboard'
  })
  const [pageParams, setPageParams] = useState(() => {
    // Передаём OAuth код в настройки камер
    if (window.location.pathname === '/ivideon-callback') {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const state = params.get('state')
      if (code && state) {
        // Чистим URL чтобы не было мусора
        window.history.replaceState({}, '', '/')
        return { tab: 'cameras', ivideonOAuthCode: code, ivideonOAuthState: state }
      }
    }
    if (window.location.pathname === '/daily-report') {
      const params = new URLSearchParams(window.location.search)
      const histState = window.history.state
      const pvzId = params.get('pvzId') || histState?.pvzId || null
      const date = params.get('date') || histState?.date || null
      const sessionRaw = safeSessionStorage.getItem('wb_daily_report_return')
      let sessionData = null
      if (sessionRaw) {
        try {
          sessionData = JSON.parse(sessionRaw)
          if (sessionData && (sessionData.pvzId !== pvzId || sessionData.date !== date)) {
            sessionData = null
          }
        } catch {}
      }
      return {
        pvzId,
        date,
        returnTo: histState?.returnTo || sessionData?.returnTo || null,
        returnParams: histState?.returnParams || sessionData?.returnParams || null
      }
    }
    if (window.location.pathname === '/reports') {
      const params = new URLSearchParams(window.location.search)
      return {
        pvzId: params.get('pvzId') || null,
        month: params.get('month') || null,
        from: params.get('from') || null,
        to: params.get('to') || null
      }
    }
    return null
  })
  const [pinUnlocked, setPinUnlocked] = useState(() => safeSessionStorage.getItem('pin_unlocked') === '1')
  const [hasRedirected, setHasRedirected] = useState(false)
  const [forcedLoginInput, setForcedLoginInput] = useState('')
  const [forcedLoginError, setForcedLoginError] = useState('')
  const [isSavingLogin, setIsSavingLogin] = useState(false)

  // Очистка параметра ?t= из URL для чистого вида
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('t')) {
        url.searchParams.delete('t')
        window.history.replaceState({}, '', url.pathname + url.search + url.hash)
      }
    } catch (e) {
      console.warn('Failed to clean URL parameters:', e)
    }
  }, [])

  // Перенаправление сотрудников в операции при старте
  useEffect(() => {
    if (profile && !hasRedirected) {
      if (!isManagement) {
        setActivePage('operations')
      }
      setHasRedirected(true)
    }
  }, [profile, hasRedirected, isManagement])

  const [isRecovery, setIsRecovery] = useState(window.location.hash.includes('type=recovery'))

  const handlePageChange = useCallback((page, params, options = {}) => {
    setActivePage(page)
    setPageParams(params || null)
    const replace = Boolean(options && options.replace)
    if (page === 'daily_report') {
      const searchParams = new URLSearchParams()
      if (params?.pvzId) searchParams.set('pvzId', params.pvzId)
      if (params?.date) searchParams.set('date', params.date)
      const queryStr = searchParams.toString()
      const newUrl = queryStr ? `/daily-report?${queryStr}` : '/daily-report'
      const navState = {
        page: 'daily_report',
        pvzId: params?.pvzId || null,
        date: params?.date || null,
        returnTo: params?.returnTo || null,
        returnParams: params?.returnParams || null
      }
      if (params?.returnTo) {
        safeSessionStorage.setItem('wb_daily_report_return', JSON.stringify({
          returnTo: params.returnTo,
          returnParams: params.returnParams || null,
          pvzId: params.pvzId || null,
          date: params.date || null
        }))
      } else {
        safeSessionStorage.removeItem('wb_daily_report_return')
      }
      if (replace) {
        window.history.replaceState(navState, '', newUrl)
      } else {
        window.history.pushState(navState, '', newUrl)
      }
    } else if (page === 'reports') {
      const searchParams = new URLSearchParams()
      if (params?.pvzId) searchParams.set('pvzId', params.pvzId)
      if (params?.month) searchParams.set('month', params.month)
      const queryStr = searchParams.toString()
      const newUrl = queryStr ? `/reports?${queryStr}` : '/reports'
      const navState = {
        page: 'reports',
        pvzId: params?.pvzId || null,
        month: params?.month || null
      }
      if (replace) {
        window.history.replaceState(navState, '', newUrl)
      } else {
        window.history.pushState(navState, '', newUrl)
      }
    } else if (window.location.pathname === '/daily-report' || window.location.pathname === '/reports') {
      if (replace) {
        window.history.replaceState(null, '', '/')
      } else {
        window.history.pushState(null, '', '/')
      }
    }
  }, [])

  const handleDailyReportBack = useCallback(() => {
    const sessionRaw = safeSessionStorage.getItem('wb_daily_report_return')
    const histState = window.history.state
    const target = resolveDailyReportBackTarget(pageParams, histState, sessionRaw, isManagement)
    safeSessionStorage.removeItem('wb_daily_report_return')
    handlePageChange(target.page, target.params, { replace: true })
  }, [pageParams, isManagement, handlePageChange])

  useEffect(() => {
    const handlePopState = (e) => {
      const pathname = window.location.pathname
      const searchParams = new URLSearchParams(window.location.search)
      if (pathname === '/daily-report') {
        const pvzId = searchParams.get('pvzId') || e.state?.pvzId || null
        const date = searchParams.get('date') || e.state?.date || null
        const sessionRaw = safeSessionStorage.getItem('wb_daily_report_return')
        let sessionData = null
        if (sessionRaw) {
          try {
            sessionData = JSON.parse(sessionRaw)
            if (sessionData && (sessionData.pvzId !== pvzId || sessionData.date !== date)) {
              sessionData = null
            }
          } catch {}
        }
        setActivePage('daily_report')
        setPageParams({
          pvzId,
          date,
          returnTo: e.state?.returnTo || sessionData?.returnTo || null,
          returnParams: e.state?.returnParams || sessionData?.returnParams || null
        })
      } else {
        safeSessionStorage.removeItem('wb_daily_report_return')
        if (pathname === '/reports') {
          const pvzId = searchParams.get('pvzId') || e.state?.pvzId || null
          const month = searchParams.get('month') || e.state?.month || null
          setActivePage('reports')
          setPageParams({
            pvzId,
            month
          })
        } else if (pathname === '/') {
          const savedPage = safeLocalStorage.getItem('wb_active_page') || 'dashboard'
          setActivePage(savedPage)
          setPageParams(null)
        }
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (activePage !== 'daily_report') {
      localStorage.setItem('wb_active_page', activePage)
    }
  }, [activePage])

  useEffect(() => {
    const handleDailyReportNavigate = (e) => {
      if (e.detail) {
        handlePageChange('daily_report', e.detail)
      }
    }
    window.addEventListener('daily-report-navigate', handleDailyReportNavigate)
    return () => window.removeEventListener('daily-report-navigate', handleDailyReportNavigate)
  }, [handlePageChange])

  useEffect(() => {
    const handleHashChange = () => {
      setIsRecovery(window.location.hash.includes('type=recovery'))
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  const handleTourNavigate = useCallback((page, params) => {
    handlePageChange(page, params)
  }, [handlePageChange])

  // Loading state
  if (loading || (user && !profile)) {
    return (
      <div className="app-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px', width: '100%', textAlign: 'center' }}>
        <div className="app-loading-spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ margin: 0, padding: 0, textAlign: 'center', width: '100%' }}>Загрузка...</p>
      </div>
    )
  }

  // Not authenticated or in recovery mode
  const urlParams = new URLSearchParams(window.location.search);
  const hasInvite = urlParams.has('invite');

  if ((!user && !isDemoMode) || isRecovery) {
    if (window.location.pathname === '/login' || hasInvite) {
      return <LoginPage onBack={() => window.location.href = '/'} />
    }
    if (window.location.pathname === '/register') {
      return <LoginPage onBack={() => window.location.href = '/'} initialView="register" />
    }
    if (window.location.pathname === '/reset-password') {
      return <LoginPage onBack={() => window.location.href = '/'} initialView="reset" />
    }
    return (
      <LandingPage 
        onLogin={() => window.location.href = '/login'} 
        onRegister={() => window.location.href = '/register'} 
        isAuthenticated={!!user || isDemoMode} 
      />
    )
  }

  // Роут /site — показывает лендинг для залогиненных пользователей
  if (window.location.pathname === '/site') {
    const goBack = () => { window.history.replaceState({}, '', '/'); window.location.reload() }
    return (
      <LandingPage 
        onLogin={goBack} 
        onRegister={goBack}
        isAuthenticated={!!user || isDemoMode}
      />
    )
  }

  // Not approved (skip for demo)
  if (!isDemoMode && effectiveProfile && !effectiveProfile.is_approved) {
    const isRejected = effectiveProfile?.full_name?.startsWith('REJECTED_');
    return (
      <div className="app-pending">
        <div className="app-pending-card card animate-scale-in" style={{ borderColor: isRejected ? 'var(--danger)' : 'var(--surface-border)' }}>
          <div className="app-pending-icon" style={{ color: isRejected ? 'var(--danger)' : 'var(--wb-primary)', marginBottom: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isRejected ? (
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--danger)', transformOrigin: 'center' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            ) : (
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--wb-primary)' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="12" x2="12" y2="7" className="animate-spin-hour" style={{ transformOrigin: '12px 12px', strokeWidth: '2' }} />
                <line x1="12" y1="12" x2="12" y2="5.5" className="animate-spin-second" style={{ transformOrigin: '12px 12px', strokeWidth: '1.2', stroke: 'currentColor' }} />
              </svg>
            )}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', textAlign: 'center' }}>
            {isRejected ? 'Заявка отклонена' : 'Ожидание одобрения'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.5, textAlign: 'center', marginBottom: '24px' }}>
            {isRejected 
              ? 'К сожалению, владелец ПВЗ отклонил вашу заявку на регистрацию в системе. Пожалуйста, свяжитесь с ним для уточнения деталей.' 
              : 'Ваша заявка на регистрацию отправлена администратору. Доступ откроется автоматически в реальном времени, как только владелец её одобрит.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
            {!isRejected && (
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => window.location.reload()}>
                Обновить статус
              </button>
            )}
            <button className="btn btn-ghost text-danger" style={{ flex: 1, border: '1px solid rgba(239, 68, 68, 0.2)' }} onClick={signOut}>
              Вернуться ко входу
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Mandatory Login Creation for existing users (skip for demo)
  if (!isDemoMode && effectiveProfile && !effectiveProfile.login) {
    const handleSetLogin = async (e) => {
      e.preventDefault()
      setForcedLoginError('')
      setIsSavingLogin(true)
      try {
        const loginStr = forcedLoginInput.trim().toLowerCase()
        if (!loginStr) throw new Error('Логин не может быть пустым')
        const loginRegex = /^[a-zA-Z0-9_-]+$/
        if (!loginRegex.test(loginStr)) throw new Error('Только латинские буквы, цифры, дефис и подчеркивание')

        // Assuming apiClient exists or replace with fetch/supabase call
        const { data: profileData, error } = await supabase
          .from('profiles')
          .update({ login: loginStr })
          .eq('id', profile.id)
          .select()
          .single()

        if (error) throw error
        profile.login = profileData.login
      } catch (err) {
        setForcedLoginError(err.message)
      } finally {
        setIsSavingLogin(false)
      }
    }

    return (
      <div className="app-pending">
        <form className="app-pending-card card animate-scale-in" onSubmit={handleSetLogin} style={{ textAlign: 'left', padding: '32px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '16px' }}>Придумайте логин</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px', textAlign: 'center' }}>
            Мы переходим на вход по логину вместо email. Пожалуйста, задайте уникальный логин для вашего аккаунта. В будущем вы сможете входить в систему, используя его.
          </p>
          <div className="input-group">
            <label>Ваш логин</label>
            <input 
              className="input" 
              type="text" 
              placeholder="ivan_petrov" 
              value={forcedLoginInput} 
              onChange={e => setForcedLoginInput(e.target.value)} 
              required 
            />
          </div>
          {forcedLoginError && <div style={{ color: 'var(--danger)', fontSize: '13px', marginTop: '8px', marginBottom: '16px', padding: '8px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '6px' }}>{forcedLoginError}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
            <button type="button" className="btn btn-ghost" onClick={signOut}>
              Выйти
            </button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={isSavingLogin}>
              {isSavingLogin ? 'Сохранение...' : 'Сохранить логин'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  // PIN Setup Screen — если пин еще не установлен (skip for demo / impersonation / superadmin)
  const isSuperAdmin = effectiveProfile?.email === 'sarkis.avakian20032@mail.ru' || effectiveProfile?.login === 'sarkis20032';
  const isImpersonating = !!safeSessionStorage.getItem('superadmin_token')
  const isSecureContext = window.isSecureContext && !isImpersonating && !isSuperAdmin
  if (!isDemoMode && effectiveProfile && !effectiveProfile.pin_hash && isSecureContext) {
    return (
      <PinSetupScreen 
        profile={profile} 
        onComplete={() => {
          fetchProfile()
          setPinUnlocked(true)
          safeSessionStorage.setItem('pin_unlocked', '1')
        }} 
      />
    )
  }

  // PIN Lock Screen (skip for demo / impersonation)
  if (!isDemoMode && effectiveProfile && effectiveProfile.pin_hash && !pinUnlocked && isSecureContext) {
    return (
      <PinLockScreen 
        profile={profile} 
        onUnlock={() => {
          safeSessionStorage.setItem('pin_unlocked', '1')
          setPinUnlocked(true)
        }}
        onLogout={() => {
          safeSessionStorage.removeItem('pin_unlocked')
          signOut()
        }}
      />
    )
  }

  let resolvedPage = activePage
  if ((resolvedPage === 'dashboard' || resolvedPage === 'daily_report' || resolvedPage === 'reports') && !isManagement) {
    resolvedPage = 'operations'
  } else if (resolvedPage === 'superadmin' && !isSuperAdmin) {
    resolvedPage = isManagement ? 'dashboard' : 'operations'
  }

  // Page content
  const renderPage = () => {
    switch (resolvedPage) {
      case 'dashboard':
        return <DashboardPage onPageChange={handlePageChange} />
      case 'chat':
        return <ChatPage />
      case 'ai_assistant':
        return <AiAssistantPage />
      case 'operations':
        return <OperationsPage />
      case 'payments':
        return <PaymentsPage />
      case 'schedule':
        return <SchedulePage />
      case 'analytics':
        return <AnalyticsPage />
      case 'tasks':
        return <TasksPage />
      case 'cameras':
        return <CamerasPage onPageChange={handlePageChange} />
      case 'vacancies':
        return <VacanciesPage />
      case 'support':
        return <SupportPage />
      case 'superadmin':
        if (!isSuperAdmin) return null
        return <SuperAdminPage />
      case 'daily_report':
        return (
          <DailyReportPage
            pageParams={pageParams}
            onBack={handleDailyReportBack}
          />
        )
      case 'reports':
        return (
          <ReportsPage
            onPageChange={handlePageChange}
            pageParams={pageParams}
          />
        )
      case 'settings':
        return <SettingsPage onPageChange={handlePageChange} initialTab={pageParams?.tab} pageParams={pageParams} onTabConsumed={() => setPageParams(null)} />
      default:
        return null
    }
  }

  // Lock screen — сбрасывает PIN без полного logout
  const lockScreen = () => {
    safeSessionStorage.removeItem('pin_unlocked')
    setPinUnlocked(false)
  }

  return (
    <TourProvider onNavigate={handleTourNavigate}>
      <AppLayout activePage={resolvedPage} onPageChange={handlePageChange} onLockScreen={effectiveProfile?.pin_hash ? lockScreen : null}>
        {renderPage()}
      </AppLayout>
      <TourGuide />
    </TourProvider>
  )
}

export default function App() {
  const pathname = window.location.pathname
  if (pathname.startsWith('/vacancy/')) {
    const slug = pathname.split('/vacancy/')[1]
    if (slug) {
      return (
        <ErrorBoundary>
          <ToastProvider>
            <PublicVacancyPage slug={slug} />
          </ToastProvider>
        </ErrorBoundary>
      )
    }
  }

  if (pathname.startsWith('/vacancy-response/')) {
    const responseId = pathname.split('/vacancy-response/')[1]
    if (responseId) {
      return (
        <ErrorBoundary>
          <ToastProvider>
            <PublicResponseStatusPage responseId={responseId} />
          </ToastProvider>
        </ErrorBoundary>
      )
    }
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Suspense fallback={
            <div className="app-loading">
              <div className="app-loading-spinner"></div>
              <p>Загрузка...</p>
            </div>
          }>
            <AppContent />
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
